import React, { useState, useEffect } from 'react';
import { useFormik } from 'formik';
import { useGlobal } from '@rcg/ui-react';
import { Modal, Card, CardActionArea, CardActions, CardContent } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import * as Yup from 'yup';
import clsx from 'clsx';
import { useReactiveVar } from '@apollo/client';
import Button from '../UiComponents/Button';
import FlightSearch from './components/FlightSearch';
import useStyles from './useStyles';
import {
  paramsVar,
  searchParamsVar,
  browseParamsVar,
} from '../../operations/localState/reactiveVariables';
import { setBrowseParams } from '../../operations/localState/setSearchParams';
import setParams from '../../operations/localState/setParams';
import {
  getSessionStorageItem,
  setSessionStorageItem,
} from '../../operations/sessionStorage/utils';
import useDictionary from '../../hooks/useDictionary';
import {
  SESSION_STORAGE_CURRENT_SAILING,
  SESSION_STORAGE_CHANGE_FLIGHTS,
  CHANGE_FLIGHT_MODE,
  SESSION_STORAGE_TOTAL_GUEST_SELECTED_FLIGHT,
  FLIGHT_TYPE_CODES,
  SESSION_STORAGE_FLIGHT_TYPE_CODE,
  SESSION_STORAGE_BOOKING_PROFILE_NAME,
} from '../../utils/constants';
import commonUtils from '../../utils/common';
import flightSearchUtils from '../../utils/flightSearch';
import Dictionary from '../../utils/dictionary';

export const getInitialValues = (
  arrivalAirportCode,
  bookingID,
  currency,
  departureAirport,
  flightDates,
  selectedGuests,
  flightType,
  arrivalAirport,
  isMulticity,
) => {
  const returnDepartureCode = () => {
    if (isMulticity) {
      return null;
    }
    if (arrivalAirport?.code) {
      return arrivalAirport?.code;
    }
    return arrivalAirportCode;
  };
  return {
    arrivalAirportCode: arrivalAirport ? arrivalAirport?.code : arrivalAirportCode,
    returnDepartureAirportCode: returnDepartureCode(),
    bookingID,
    currency: currency || '',
    departureAirportCode: departureAirport ? departureAirport.code : null,
    departureDate: flightDates ? flightDates.endDate : null,
    arrivalDate: flightDates ? flightDates.startDate : null,
    numberOfGuests: selectedGuests?.length,
    guestIDs: selectedGuests?.reduce((filtered, guest) => {
      if (guest.id && guest.selected && guest.requiresFlightTicket) {
        filtered.push(guest.id);
      }
      return filtered;
    }, []),
    type: flightType,
  };
};

const getShipInfo = (currentSailingFromAmend, lastSearchParams, browseParams) => {
  const sailings = getSessionStorageItem(SESSION_STORAGE_BOOKING_PROFILE_NAME, true);
  let ship = '';
  if (currentSailingFromAmend?.ship) {
    ship = currentSailingFromAmend.ship.name;
  }
  if (lastSearchParams?.ship) {
    ship = lastSearchParams.ship.label;
  }
  if (browseParams?.ship) {
    ship = browseParams.ship.label;
  }
  if (sailings?.currentBooking?.sailing?.itinerary?.ship?.name) {
    ship = sailings?.currentBooking?.sailing?.itinerary?.ship?.name;
  }
  return ship;
};

const FlightSearchForm = (props) => {
  const {
    setDepartureAirportCode,
    isFromBrowseSearch,
    submitFlightSearch,
    className,
    arrivalAirportCode,
    bookingID,
    currency,
    incomingStartDate,
    incomingEndDate,
    flightToDefault,
    labelFrom,
    labelTo,
    labelDatepicker,
    labelButton,
    noResultsErrorMessage,
    placeholderFrom,
    minAvailableDate,
    maxAvailableDate,
    acOptions,
    menuItems,
    showPassengersCounter,
    handleGuestsCounter,
    numberOfGuestCounter,
    setFlightDatesBrowse,
    setSelectedFlightType,
    clearSearchResults = () => {},
  } = props;

  const classes = useStyles();
  const {
    data: { localizedDayjs },
  } = useGlobal();
  const lastSearchParams = useReactiveVar(searchParamsVar);
  const browseParams = useReactiveVar(browseParamsVar);
  const lastParams = useReactiveVar(paramsVar);
  const [departureAirport, setDepartureAirport] = useState(null);
  const [arrivalAirport, setArrivalAirport] = useState(null);
  const [flightDates, setFlightDates] = useState(null);
  const [open, setOpen] = useState(false);
  const [flightType, setFlightType] = useState(
    getSessionStorageItem(SESSION_STORAGE_FLIGHT_TYPE_CODE),
  );
  const guestMenuItems = menuItems.find((menuItem) => menuItem.type === 'guestMenu');
  const [selectedGuests, setSelectedGuests] = useState(guestMenuItems ? guestMenuItems.items : []);
  const lastMenuItems = [...menuItems];
  let departureAirportObject = {};
  let arrivalAriportObject = {};
  let lastStartDate = '';
  let lastEndDate = '';
  const currentSailingFromAmend = getSessionStorageItem(SESSION_STORAGE_CURRENT_SAILING);
  const isEnableChangeFlight = getSessionStorageItem(SESSION_STORAGE_CHANGE_FLIGHTS);
  const ship = getShipInfo(currentSailingFromAmend, lastSearchParams, browseParams);
  const lastGuestMenuItems = lastMenuItems.find((menuItem) => menuItem.type === 'guestMenu');
  const getDictionary = useDictionary(Dictionary);
  const sailingInfo = getSessionStorageItem(SESSION_STORAGE_BOOKING_PROFILE_NAME, true);

  const itineraryDays = sailingInfo?.currentBooking?.sailing?.itinerary?.days;
  const nearByAirportsProfile = sailingInfo?.flightProfile?.nearByAirports?.[0];

  const checkIfNearBySelectedAirport = (nearByAirportsInfo) => {
    const nearestAirport = sailingInfo?.flightProfile?.nearestAirport?.arrival?.code;
    const nearByAirports = nearByAirportsInfo ? nearByAirportsInfo?.nearByAirports : [];
    const airportsArray = [...nearByAirports, nearestAirport];

    const isNearBySelectedAirport = airportsArray?.find(
      (airport) => airport === arrivalAirport?.code,
    );

    if (!isNearBySelectedAirport) {
      setOpen(true);
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  const embarkDay = itineraryDays?.find(
    (day) => day.ports && day.ports.some((port) => port.activity === 'EMBARK'),
  );

  const debarkDay = itineraryDays?.find(
    (day) => day.ports && day.ports.some((port) => port.activity === 'DEBARK'),
  );

  const getRouteType = () => {
    if (embarkDay && debarkDay) {
      const embarkPortCode = embarkDay?.ports?.find((port) => port.activity === 'EMBARK')?.port
        ?.code;
      const debarkPortCode = debarkDay?.ports?.find((port) => port.activity === 'DEBARK')?.port
        ?.code;

      return embarkPortCode && debarkPortCode ? embarkPortCode !== debarkPortCode : null;
    }
    return null;
  };

  // The itinerary content (AEM) can be missing for a sailing, which makes getRouteType() fall
  // through to null and treat an open-jaw booking as a round trip. Fall back to the flight profile.
  const isMulticity =
    getRouteType(embarkDay, debarkDay) ||
    flightSearchUtils.isOpenJawProfile(sailingInfo?.flightProfile?.nearestAirport);

  if (!isFromBrowseSearch) {
    if (lastSearchParams) {
      if (lastGuestMenuItems) {
        departureAirportObject = acOptions?.find(
          (airport) => airport.code === lastSearchParams.departureAirportCode,
        );
        arrivalAriportObject = acOptions?.find(
          (airport) => airport.code === lastSearchParams.arrivalAirportCode,
        );
        lastStartDate = lastSearchParams.arrivalDate;
        lastEndDate = lastSearchParams.departureDate;
      }
    }
  } else if (browseParams) {
    departureAirportObject = acOptions?.find(
      (airport) => airport.code === browseParams.searchCriteria.departureAirportCode,
    );
    arrivalAriportObject = acOptions?.find(
      (airport) => airport.code === browseParams.searchCriteria?.arrivalAirportCode,
    );
    lastStartDate = browseParams.searchCriteria.startDate;
    lastEndDate = browseParams.searchCriteria.endDate;
  }

  const arrivalAirportName = arrivalAriportObject?.code
    ? commonUtils.fliyingFromOptions(arrivalAriportObject)
    : '';

  if (lastParams && lastGuestMenuItems) {
    lastGuestMenuItems.items = lastParams.selectedGuests;
  }

  const handleSelectedGuest = (value) => {
    setSelectedGuests(value);
    paramsVar({ selectedGuests: value });
    const selectedCount = value?.filter((item) => item.selected === true).length;
    setSessionStorageItem(SESSION_STORAGE_TOTAL_GUEST_SELECTED_FLIGHT, selectedCount);
    clearSearchResults();
  };

  const handleFlightType = (value) => {
    const oneWayLabel = getDictionary(Dictionary.airFlightTypeOneWay.key);
    if (value.option === oneWayLabel) {
      setFlightType(FLIGHT_TYPE_CODES.ONE_WAY);
      setSelectedFlightType(FLIGHT_TYPE_CODES.ONE_WAY);
      setSessionStorageItem(SESSION_STORAGE_FLIGHT_TYPE_CODE, FLIGHT_TYPE_CODES.ONE_WAY);
    } else {
      setSessionStorageItem(SESSION_STORAGE_FLIGHT_TYPE_CODE, FLIGHT_TYPE_CODES.ROUND_TRIP);
      setSelectedFlightType(FLIGHT_TYPE_CODES.ROUND_TRIP);
      setFlightType('');
    }
    clearSearchResults();
  };

  useEffect(() => {
    setDepartureAirport(departureAirportObject);
    if (lastGuestMenuItems) {
      setSelectedGuests(lastGuestMenuItems.items);
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    setBrowseParams({
      ...browseParams,
      searchCriteria: {
        ...browseParams?.searchCriteria,
        departureAirportCode: departureAirport?.code,
      },
    });
  }, [departureAirport]);

  const getSelectedDates = (value) => {
    const { startDate, endDate } = value;
    const formattedStartDate = localizedDayjs(startDate).format('YYYY-MM-DD');
    const formattedEndDate = localizedDayjs(endDate).format('YYYY-MM-DD');
    setFlightDates({
      startDate: formattedStartDate,
      endDate: formattedEndDate,
    });
    if (setFlightDatesBrowse) {
      setFlightDatesBrowse({
        startDate: formattedStartDate,
        endDate: formattedEndDate,
      });
    }
    clearSearchResults();
  };
  useEffect(() => {
    setFlightDates({
      startDate: lastStartDate || incomingStartDate,
      endDate: lastEndDate || incomingEndDate,
    });
    if (setFlightDatesBrowse) {
      setFlightDatesBrowse({
        startDate: lastStartDate || incomingStartDate,
        endDate: lastEndDate || incomingEndDate,
      });
    }
    // eslint-disable-next-line
  }, [incomingStartDate, incomingEndDate]);

  const flightSearchSchema = Yup.object({
    departureAirportCode: Yup.string().required('requiere'),
  });

  const initialValues = getInitialValues(
    arrivalAirportCode,
    bookingID,
    currency,
    departureAirport,
    flightDates,
    selectedGuests,
    flightType,
    arrivalAirport,
    isMulticity,
  );

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema: flightSearchSchema,
    onSubmit: (values) => {
      const requestVariables = isEnableChangeFlight
        ? { variables: { ...values, mode: CHANGE_FLIGHT_MODE } }
        : { variables: values };
      setParams(paramsVar());
      submitFlightSearch(requestVariables);
      if (arrivalAirport) {
        checkIfNearBySelectedAirport(nearByAirportsProfile);
      }
    },
  });

  return (
    <>
      <form className={clsx(className, classes.flightSearchForm)} onSubmit={formik.handleSubmit}>
        <FlightSearch
          setDepartureAirportCode={setDepartureAirportCode}
          menuItems={menuItems}
          getSelectedDates={getSelectedDates}
          selectedDepartureAirport={(value) => setDepartureAirport(value)}
          selectedArrivalAirport={(value) => setArrivalAirport(value)}
          flightToDefault={
            isEnableChangeFlight ? flightToDefault : arrivalAirportName || flightToDefault
          }
          arrivalAirportCode={arrivalAirportCode}
          bookingID={bookingID}
          incomingStartDate={incomingStartDate}
          incomingEndDate={incomingEndDate}
          incomingStartDefaultDate={lastStartDate || incomingStartDate}
          incomingEndDefaultDate={lastEndDate || incomingEndDate}
          labelFrom={labelFrom}
          labelTo={labelTo}
          labelButton={labelButton}
          resetDatesButtonLabel={getDictionary(Dictionary.datePickerResetDatesButtonLabel.key)}
          labelDatepicker={labelDatepicker}
          noResultsErrorMessage={noResultsErrorMessage}
          placeholderFrom={placeholderFrom}
          minAvailableDate={minAvailableDate}
          maxAvailableDate={maxAvailableDate}
          arrivalDate
          acOptions={acOptions}
          onSelectedGuests={(value) => handleSelectedGuest(value)}
          selectedGuests={selectedGuests}
          incomingValue={departureAirportObject}
          showPassengersCounter={showPassengersCounter}
          handleGuestsCounter={handleGuestsCounter}
          numberOfGuestCounter={numberOfGuestCounter}
          ship={ship}
          onSelectedFlightType={(value) => handleFlightType(value)}
          clearSearchResults={clearSearchResults}
          departureAirportRequiredError={Boolean(formik.errors.departureAirportCode)}
        />
      </form>
      {open && !isFromBrowseSearch && (
        <Modal
          open={open}
          onClose={handleClose}
          aria-labelledby="warning modal"
          aria-describedby="warning modal"
          disableBackdropClick
          disableEscapeKeyDown
          className={classes.modal}>
          <Card className={classes.paper}>
            <CardActionArea>
              <CardContent className={classes.contentModal}>
                <CloseIcon
                  data-testid="close-modal-amenities"
                  className={classes.closeModalIcon}
                  onClick={handleClose}
                />
                <h3>{getDictionary(Dictionary.airSearchNearAirportsWarningTitle.key)}</h3>
                <p>
                  {getDictionary(Dictionary.airSearchNearAirportsWarningDescription.key)}{' '}
                  <strong>
                    {getDictionary(Dictionary.airSearchNearAirportsWarningDescriptionEmphasis.key)}
                  </strong>
                </p>
              </CardContent>
            </CardActionArea>
            <CardActions className={classes.actionsModal}>
              <Button
                data-testid="extend-session-continue-btn"
                className={classes.submitbutton}
                onClick={handleClose}
                variant="contained"
                color="primary">
                {getDictionary(Dictionary.airSearchNearAirportsWarningButtonText.key)}
              </Button>
            </CardActions>
          </Card>
        </Modal>
      )}
    </>
  );
};

export default FlightSearchForm;
